import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useDashboardDeploymentsService } from '@/services/dashboard-deployments'
import { useUiSettingsStore } from '@/stores/ui-settings.store'
import { getErrorMessage, getErrorStatus } from '@/utils/error-handler'
import { getSelectableDeployments, isTargetInDeployments } from '@/utils/dashboardTarget'
import type { DashboardDeployment } from '@/services/dashboard-deployments'
import type { DashboardTarget } from '@/types/ui-settings.types'

/**
 * Merchant-dashboard deployments offered by the Dashboard target picker, and the check that
 * a saved deployment target is still one of them.
 */
export const useDashboardDeploymentsStore = defineStore('adx-dashboard-deployments', () => {
  const service = useDashboardDeploymentsService()

  // === State ===
  const deployments = ref<DashboardDeployment[]>([])
  const fetchedAt = ref<string | null>(null)
  /** False once the endpoint answers 404 — production, or a backend that has not shipped it. */
  const isAvailable = ref(true)
  const hasLoaded = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  /** The last fetch failed for a reason other than 404. Kept after the error toast clears `error`. */
  const hasLoadFailed = ref(false)
  /**
   * The saved deployment branch that was dropped because the loaded list no longer offers it,
   * kept until the admin dismisses the notice so it is seen rather than missed on page load.
   */
  const droppedBranch = ref<string | null>(null)

  // === Getters ===
  const hasError = computed(() => error.value !== null)
  const selectableDeployments = computed(() => getSelectableDeployments(deployments.value))

  // === Actions ===

  // GET — swallow errors (component uses useStoreErrorWatch)
  async function fetchDeployments() {
    isLoading.value = true
    error.value = null
    hasLoadFailed.value = false
    try {
      const response = await service.list()
      deployments.value = response.deployments ?? []
      fetchedAt.value = response.fetchedAt
      isAvailable.value = true
    } catch (e) {
      deployments.value = []
      // A missing endpoint is an expected state, shown in the picker rather than as an error.
      if (getErrorStatus(e) === 404) {
        isAvailable.value = false
      } else {
        hasLoadFailed.value = true
        error.value = getErrorMessage(e, 'Failed to load dashboard deployments')
      }
    } finally {
      hasLoaded.value = true
      isLoading.value = false
    }
  }

  /**
   * Falls back to Default when the saved deployment is no longer offered. Only a loaded list
   * that lacks the branch names it in `droppedBranch` and returns it, so the admin is told once.
   * A 404 means this environment offers no deployments, so the target is dropped silently; any
   * other failed fetch proves nothing either way, so the target is kept.
   */
  async function reconcileSavedTarget(): Promise<string | null> {
    const uiSettingsStore = useUiSettingsStore()
    const target = uiSettingsStore.activeDashboardTarget
    if (target.kind !== 'deployment') return null

    await fetchDeployments()
    if (hasLoadFailed.value) return null

    if (!isAvailable.value) {
      uiSettingsStore.resetDashboardTarget()
      return null
    }
    if (!isTargetInDeployments(target, deployments.value)) {
      uiSettingsStore.resetDashboardTarget()
      droppedBranch.value = target.branch
      return target.branch
    }
    return null
  }

  function dismissDroppedBranch() {
    droppedBranch.value = null
  }

  /**
   * Whether Login as merchant may send the admin token to this target. Once the list has loaded,
   * a deployment must still be in it; before that, or after a failed fetch, the saved target is
   * trusted on its URL check alone, the same as when it was picked.
   */
  function isTargetUsable(target: DashboardTarget): boolean {
    if (target.kind !== 'deployment') return true
    if (!hasLoaded.value || hasLoadFailed.value) return true
    return isAvailable.value && isTargetInDeployments(target, deployments.value)
  }

  function clearError() {
    error.value = null
  }

  function reset() {
    deployments.value = []
    fetchedAt.value = null
    isAvailable.value = true
    hasLoaded.value = false
    isLoading.value = false
    error.value = null
    hasLoadFailed.value = false
    droppedBranch.value = null
  }

  return {
    // State
    deployments,
    fetchedAt,
    isAvailable,
    hasLoaded,
    isLoading,
    error,
    hasLoadFailed,
    droppedBranch,
    // Getters
    hasError,
    selectableDeployments,
    // Actions
    fetchDeployments,
    reconcileSavedTarget,
    dismissDroppedBranch,
    isTargetUsable,
    clearError,
    reset
  }
})
